import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    BreadCrumbs,
    CommonModal as UpdateModal,
    CommonModal as DeleteModal,
    Input,
    PrimaryButton,
    SecondaryButton,
    PageTitleWithToolTip,
    AddButton,
    DataGrid,
} from '../Common';
import Routes from '../../routes';
import { Grey1, Grey2, Grey3 } from '../../color_utils';
import { Grid, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmation from '../../Icons/img_delete_confirmation.svg';
import { exportType } from '../Common/Table/utils';
import { text_commonCellProps } from '../Common/DataGrid/utils';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';

const Classifications = ({
    history: {
        push = () => { }
    } = {},
}) => {
    const [modalData, setModalData] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [newClassificationError, setnewClassificationError] = useState(null);

    const closeModal = (isDelete = false) => {
        if (isDelete) {
            return setModalData(prev => ({ ...prev, deleteOpen: false }));
        }
        setModalData(prev => ({ ...prev, editOpen: false }));
        setnewClassificationError(null);
    };

    const handleCreate = () => setModalData({
        editOpen: true,
        header: "Add New Classification",
        classificationName: "",
    });

    const resetState = () => {
        setModalData({});
        return setRefresh(!refresh);
    };

    const handleSuccess = (successType) => {
        return toast.success(`The classification has been successfully ${successType}`, { duration: 6000 });
    };

    const handleError = (error = '') => {
        toast.error('Something went wrong. Please try again.', { duration: 6000 });
        return console.log('Error', error);
    };

    const handleEdit = row => (
        setModalData({
            editOpen: true,
            header: "Edit Classification",
            classificationName: row.name,
            id: row.id,
            enabled: row.enabled,
        })
    );

    const handleDelete = row => (
        setModalData({
            deleteOpen: true,
            classificationName: row.name,
            id: row.id,
        })
    );

    const createClassification = async () => {
        if (!modalData.classificationName) {
            return setnewClassificationError("This field is required");
        }
        setnewClassificationError(null);

        const classificationData = { name: modalData.classificationName };
        await fetch(
            Routes.Instance.AddClassification(),
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(classificationData)
            }
        )
            .then(res => {
                if (res.status === 200) {
                    handleSuccess('added');
                } else {
                    toast.error('Error creating Classification. Please try again.', { duration: 6000 });
                    console.log('Response', res);
                }
            })
            .finally(() => resetState())
            .catch(err => handleError(err));
    };

    const editClassification = async (classificationEnabled = true) => {
        if (!modalData.classificationName && classificationEnabled) {
            return setnewClassificationError("This field is required");
        }
        setnewClassificationError(null);
        const classificationData = { id: modalData.id, name: modalData.classificationName, enabled: classificationEnabled };
        await fetch(
            Routes.Instance.UpdateClassification(classificationData.id),
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(classificationData)
            }
        )
            .then(res => {
                if (res.status === 200) {
                    const successType = classificationEnabled ? 'edited' : 'deleted';
                    handleSuccess(successType);
                } else {
                    toast.error('Error updating Classification. Please try again.', { duration: 6000 });
                    console.log('Response', res);
                }
            })
            .finally(() => resetState())
            .catch(err => handleError(err));
    };
    const columns = [
        {
            field: 'name',
            headerName: 'Classification Name',
            ...text_commonCellProps,
        },
        {
            field: 'numberOfLocations',
            headerName: 'Number of locations',
            filterable: false,
            flex: 1,
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Edit Classification"
                        >
                            <EditIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => handleEdit(row)}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Delete Classification"
                        >
                            <DeleteIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => handleDelete(row)}
                />
            ]
        },
    ]

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Account Details', to: `/Account` },
                    { text: 'Manage Classifications' }
                ]}
            />

            <Grid container>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle="Manage Classifications"
                        toolTipText="Manage Classifications of locations in the account. A Classification is a custom field available to Account Admin that allows them to classify types of locations within your company portfolio. Classifications may include Commercial property, Office Space, Leased Property, etc."
                    />
                </Grid>
                <Grid item xs={6} align="right">
                    <AddButton
                        text="Add Classification"
                        onClick={handleCreate}
                    />
                </Grid>
                <Grid item xs={6} sx={{ marginTop: '1%' }}>
                    <DataGrid
                        tableId="account-classifications-table"
                        exportButtonURL={Routes.Instance.ExportClassificationTable()}
                        url={Routes.Instance.GetClassificationsForTable()}
                        exportFileName="Classifications"
                        exportFileTypes={[
                            { name: 'Classification', type: exportType.pdf },
                            { name: 'Classification', type: exportType.csv }
                        ]}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Add a new Classification to get started."
                        refresh={refresh}
                        columns={columns}
                        rowCount={5}
                    />
                </Grid>
            </Grid>

            <UpdateModal
                open={modalData.editOpen}
                close={() => closeModal()}
                header={modalData.header}
            >
                <Grid
                    container
                    spacing={2}
                    width={450}
                >
                    <Grid item xs={12} align="center">
                        <Input
                            required
                            sx={{ width: "84%", mt: 1 }}
                            variant="outlined"
                            label="Classification Name"
                            error={newClassificationError}
                            value={modalData.classificationName}
                            onChange={e => setModalData(prev => ({ ...prev, classificationName: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} align="center" marginBottom={5}>
                        <PrimaryButton
                            useLargeButton
                            width={84}
                            buttonText={modalData.id ? 'Edit' : 'Add'}
                            onClick={() => modalData.id ?
                                editClassification(true) :
                                createClassification()
                            }
                        />
                    </Grid>
                </Grid>
            </UpdateModal>

            <DeleteModal
                open={modalData.deleteOpen}
                close={() => closeModal(true)}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} align='center'>
                        <img
                            style={{ paddingTop: '1%', paddingBottom: '5%' }}
                            src={DeleteConfirmation}
                            width="275px"
                            height="250px"
                            alt="Are you sure you want to delete this classification?"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { color: Grey1 } }}
                            align='center'
                        >
                            Delete this classification ?
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { fontSize: 14, color: Grey3 } }}
                            align='center'
                        >
                            It will be permanently lost.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <PrimaryButton
                            useLargeButton
                            buttonText='Yes, Delete'
                            onClick={() => editClassification(false)}
                            width={75}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <SecondaryButton
                            useLargeButton
                            buttonText='Cancel'
                            onClick={() => setModalData(prev => ({ ...prev, deleteOpen: false }))}
                            width={75}
                        />
                    </Grid>
                </Grid>
            </DeleteModal>
        </>
    );
};

export default Classifications;
